/**
 * Vues du dashboard CLEVEL — predicates centralisés.
 *
 * Demande CEO 2026-05-04 (et affinée 2026-05-07) : "chaque vignette doit être
 * cliquable et montrer la liste des biens sélectionnés dans la vignette".
 *
 * Source de vérité unique : chaque tuile du Dashboard utilise `matchView`
 * pour COMPTER, et la BiensListPage utilise EXACTEMENT le même `matchView`
 * pour FILTRER après navigation `/biens?view=<key>(&collab=<id>)`. Pas de
 * dérive possible entre le compteur affiché et la liste cible.
 *
 * À ajouter quand le data model l'aura :
 * - "published-stale-active" : biens publiés avec modifs non poussées.
 *   Nécessite `publishedSnapshot` ou `lastPublishedAt` vs `updatedAt`.
 *   Pour l'instant le predicate retourne `false` (compteur = 0).
 */

import type { Bien } from "@/types/bien";
import { validateBienPrep, type BienDraft } from "@/lib/bienPrepValidation";
import { hasUnpublishedChanges } from "@/lib/publicationHelpers";
import { isTransfertElecFait, computeStatutEls } from "@/lib/elsStatus";
import { today } from "@/lib/today";
import { isPebExpiringSoon } from "@/lib/pebCycle";

export type DashboardView =
  // Section 1 — Vue générale
  | "all"                        // Biens en DB
  | "prep"                       // EN PRÉPARATION
  | "dispo"                      // DISPONIBLE
  | "loue"                       // LOUÉ
  | "vendu"                      // VENDU
  | "vides"                      // EN PRÉPARATION + DISPONIBLE (= "non loués" actifs, denominateur du taux de remplissage)
  | "peb-absent-all"             // PEB absent (tous)
  | "peb-absent-prep"            // PEB absent (EN PRÉPARATION)
  | "peb-absent-active"          // PEB absent (EN PRÉPARATION + DISPONIBLE)
  | "peb-expirant"               // PEB expirant <6 mois
  | "dispo-1m"                   // DISPONIBLE depuis ≥ 1 mois
  | "dispo-3m"                   // DISPONIBLE depuis ≥ 3 mois
  | "dispo-6m"                   // DISPONIBLE depuis ≥ 6 mois
  | "incomplete"                 // Biens incomplets pour mailing
  | "mailing-ok"                 // Biens mailing prêts
  | "els-cloture-pending"        // ELS passées à clôturer — finalisation SRS pas faite (global)
  | "els-final-closure-pending"  // ELS clôturées à clôturer — finalisation SRS faite, dossier final pas fermé (global)
  // Section 2 — Par collaborateur (collab requis dans ctx)
  | "els-avenir-ok-collab"       // ELS à venir, OK (date fixée)
  | "els-avenir-issue-collab"    // ELS à venir, à gérer (date pas fixée)
  | "els-cloture-pending-collab" // ELS passées à clôturer (par collab)
  | "els-final-closure-pending-collab" // ELS clôturées à clôturer (par collab)
  | "peb-a-gerer-collab"         // PEB à gérer (absent ou expirant) en location active, par collab
  | "transfert-pas-fait-collab"  // ELS effectué (date passée) mais transfert compteur pas coché → alerte, par collab
  | "photos-pas-pret-collab"     // ELS effectué (date passée) mais "appartement prêt photos" toujours non saisi (null) → alerte, par collab
  // Section 3 — Publication (sur biens EN PRÉPARATION + DISPONIBLE uniquement)
  | "published-active"
  | "draft-active"
  | "published-stale-active";    // TODO: data not tracked yet

export interface ViewContext {
  collabId?: string;
}

const isPrep = (b: Bien): boolean =>
  (b.disponibilite || "").toUpperCase() === "EN PRÉPARATION";

const isDispo = (b: Bien): boolean =>
  (b.disponibilite || "").toUpperCase() === "DISPONIBLE";

const isActive = (b: Bien): boolean => isPrep(b) || isDispo(b);

/**
 * Nombre de jours écoulés depuis que le bien est passé en DISPONIBLE.
 * Source : `seraDispoPour` (date à partir de laquelle l'appart est libre).
 * Retourne null si le bien n'est pas DISPONIBLE, si la date est absente,
 * invalide, ou encore future (dispo annoncée en avance).
 *
 * Aligné sur `getDispoDepuis()` dans BiensListPage (colonne "Dispo depuis X
 * jours" — seuils 30/90/180 = 1/3/6 mois).
 */
const daysSinceDispo = (b: Bien): number | null => {
  if ((b.disponibilite || "").toUpperCase() !== "DISPONIBLE") return null;
  if (!b.seraDispoPour) return null;
  const dispoDate = new Date(b.seraDispoPour);
  if (isNaN(dispoDate.getTime())) return null;
  const now = today();
  if (dispoDate > now) return null;
  const msPerDay = 24 * 60 * 60 * 1000;
  return Math.max(0, Math.floor((now.getTime() - dispoDate.getTime()) / msPerDay));
};

/**
 * "ELS passée à clôturer" : ELS opérationnellement réalisé (statutEls
 * = els_realise OU transfert_fait) mais le dossier SRS n'est pas finalisé
 * (ni dossierCloture, ni dossierClos).
 *
 * Spec user 2026-05-21 : le filtre transaction (=== "location") a ete retire —
 * l ELS est requis des qu un collab est assigne, peu importe la transaction
 * (vente incluse).
 */
const isElsCloturePending = (b: Bien): boolean => {
  // `statutEls` retiré (mig. 112) : colonne vide sur les 1574 biens, donc
  // cette vue ne remontait jamais rien. Statut CALCULÉ désormais.
  const els = computeStatutEls(b);
  const elsDone = els === "els_realise" || els === "transfert_fait";
  return elsDone && !b.dossierCloture && !b.dossierClos;
};

/**
 * "ELS clôturée à clôturer" : la finalisation SRS a été faite par le collab
 * (dossierCloture = true) mais la clôture finale opérationnelle n'a pas
 * encore eu lieu (dossierClos = false). Toutes transactions (cf. user 2026-05-21).
 */
const isElsFinalClosurePending = (b: Bien): boolean => {
  return b.dossierCloture === true && !b.dossierClos;
};

const isMailingComplete = (b: Bien): boolean => {
  const v = validateBienPrep(b as unknown as BienDraft);
  return v.isInPrep && v.requiredMissing.length + v.mailingMissing.length === 0;
};

const isMailingIncomplete = (b: Bien): boolean => {
  const v = validateBienPrep(b as unknown as BienDraft);
  return v.isInPrep && v.requiredMissing.length + v.mailingMissing.length > 0;
};

export function matchView(b: Bien, view: DashboardView, ctx?: ViewContext): boolean {
  switch (view) {
    case "all": return true;
    case "prep": return isPrep(b);
    case "dispo": return isDispo(b);
    case "loue": return (b.disponibilite || "").toUpperCase() === "LOUÉ";
    case "vendu": return (b.disponibilite || "").toUpperCase() === "VENDU";
    case "vides": return isActive(b);

    case "peb-absent-all": return !b.classePeb;
    case "peb-absent-prep": return isPrep(b) && !b.classePeb;
    // Demande user 2026-05-08 : "PEB absent — en location active" = uniquement
    // les DISPONIBLES (pas EN PRÉPARATION). Les biens EN PRÉP sans PEB restent
    // dans la tuile "PEB absent — en préparation". Le total `peb-absent-all`
    // reste inchangé (somme des deux + vendus/loués sans PEB).
    case "peb-absent-active": return isDispo(b) && !b.classePeb;
    // Tuile "expirant" uniquement : `classePeb` requis car le cas "classe
    // absente" est déjà compté par les tuiles peb-absent-* (pas de doublon).
    case "peb-expirant": return !!b.classePeb && isPebExpiringSoon(b.pebFinLe, b.classePeb);

    case "dispo-1m": { const d = daysSinceDispo(b); return d !== null && d >= 30; }
    case "dispo-3m": { const d = daysSinceDispo(b); return d !== null && d >= 90; }
    case "dispo-6m": { const d = daysSinceDispo(b); return d !== null && d >= 180; }

    case "incomplete": return isMailingIncomplete(b);
    case "mailing-ok": return isMailingComplete(b);

    case "els-cloture-pending": return isElsCloturePending(b);
    case "els-final-closure-pending": return isElsFinalClosurePending(b);

    case "els-avenir-ok-collab":
      return !!ctx?.collabId
        && b.collaborateurSrs === ctx.collabId
        && isActive(b)
        && computeStatutEls(b) === "date_fixee";
    case "els-avenir-issue-collab":
      return !!ctx?.collabId
        && b.collaborateurSrs === ctx.collabId
        && isActive(b)
        && computeStatutEls(b) === "avertit";
    case "els-cloture-pending-collab":
      return !!ctx?.collabId
        && b.collaborateurSrs === ctx.collabId
        && isElsCloturePending(b);
    case "els-final-closure-pending-collab":
      return !!ctx?.collabId
        && b.collaborateurSrs === ctx.collabId
        && isElsFinalClosurePending(b);
    case "peb-a-gerer-collab":
      // Biens du collab en location active (EN PRÉP + DISPO) dont le PEB est
      // absent OU va expirer sous 6 mois. Critère opérationnel : ce sont les
      // biens où le collab doit faire quelque chose côté énergie.
      return !!ctx?.collabId
        && b.collaborateurSrs === ctx.collabId
        && isActive(b)
        && isPebExpiringSoon(b.pebFinLe, b.classePeb);
    case "transfert-pas-fait-collab": {
      // Alerte transfert compteur pas fait : l'ELS a eu lieu (date
      // `edlDatePrevue` remplie et passée) mais le transfert élec n'est pas
      // acté. "Acté" = via la DATE `transfertElecDate` (mig 048) OU le boolean
      // legacy → on délègue à isTransfertElecFait (sinon on sur-compte les
      // biens dont le transfert a été saisi par date mais pas via la case).
      if (!ctx?.collabId || b.collaborateurSrs !== ctx.collabId) return false;
      if (isTransfertElecFait(b)) return false;
      if (!b.edlDatePrevue) return false;
      const d = new Date(b.edlDatePrevue);
      if (isNaN(d.getTime())) return false;
      return d <= today();
    }
    case "photos-pas-pret-collab": {
      // Alerte "appartement prêt photos" non tranché : l'ELS a eu lieu (date
      // `edlDatePrevue` remplie et passée) mais le champ tri-état
      // `appartPretPhotos` est toujours à null (—). false = "Non explicite"
      // ne déclenche PAS l'alerte (le collab a tranché). Le collab doit
      // statuer Oui/Non pour que Planning Photo puisse organiser le shoot
      // (toutes transactions — spec user 2026-05-21).
      if (!ctx?.collabId || b.collaborateurSrs !== ctx.collabId) return false;
      if (b.appartPretPhotos !== null && b.appartPretPhotos !== undefined) return false;
      if (!b.edlDatePrevue) return false;
      const d = new Date(b.edlDatePrevue);
      if (isNaN(d.getTime())) return false;
      return d <= today();
    }

    case "published-active": return isActive(b) && b.statutPublication === "Publié";
    case "draft-active": return isActive(b) && b.statutPublication === "Brouillon";
    case "published-stale-active": {
      // Publié ET en location active ET modifié depuis la dernière acceptation.
      // Cf. helper hasUnpublishedChanges() — source de vérité unique partagée
      // avec la bannière fiche, la pastille Suivi Publication et le useBlocker.
      return isActive(b) && hasUnpublishedChanges(b);
    }
  }
}

/**
 * Taux de remplissage (Section 1, tuile non standard).
 * % de loués sur le total "actifs" (loués + EN PRÉPARATION + DISPONIBLE).
 * Les vendus sont exclus du dénominateur car hors périmètre location.
 */
export function tauxRemplissage(biens: Bien[]): number {
  let loues = 0;
  let vides = 0;
  for (const b of biens) {
    const dispo = (b.disponibilite || "").toUpperCase();
    if (dispo === "LOUÉ") loues++;
    else if (dispo === "EN PRÉPARATION" || dispo === "DISPONIBLE") vides++;
  }
  const denom = loues + vides;
  if (denom === 0) return 0;
  return Math.round((loues / denom) * 100);
}

/** Pages cibles supportées pour les tuiles dashboard. */
export type ViewTargetPage = "biens" | "suivi-els" | "apparts-for-rent";

/**
 * Construit l'URL de navigation vers la page cible avec `?view=<key>` (et
 * optionnellement `&collab=<id>` pour les vues par collaborateur).
 *
 * Page par défaut : `/biens` (cas historique). Demande user 2026-05-08 :
 * certaines tuiles redirigent vers `/suivi-els` ou `/apparts-for-rent` pour
 * exposer la donnée dans son contexte métier naturel.
 */
export function viewToUrl(
  view: DashboardView,
  opts?: { collabId?: string; page?: ViewTargetPage },
): string {
  const page = opts?.page ?? "biens";
  const params = new URLSearchParams();
  params.set("view", view);
  if (opts?.collabId) params.set("collab", opts.collabId);
  return `/${page}?${params.toString()}`;
}
